import React, { useState, useEffect } from "react";
import { useKycStore } from "../../stores/useKycStore";
import Checkbox from "../../components/shared/Checkbox";
import { getUserIsUs } from "../../api/api";
import { useSDKConfigStore } from "../../stores/sdkConfigStore";

// Link constants
const LINKS = {
  US_TERMS:
    "https://docs.brahma.fi/brahma-cards-or-legal/brahmafi-cardholder-terms-u.s",
  INTERNATIONAL_TERMS:
    "https://docs.brahma.fi/brahma-cards-or-legal/brahmafi-cardholder-terms-international",
  US_ACCOUNT_OPENING:
    "https://docs.brahma.fi/brahma-cards-or-legal/brahmafi-glba-disclosure-notice",
  PROHIBITED_ACTIVITIES:
    "https://docs.brahma.fi/brahma-cards-or-legal/prohibited-activities-and-restricted-transactions-policy",
  E_SIGN_CONSENT:
    "https://docs.brahma.fi/brahma-cards-or-legal/e-sign-and-electronic-communications-notice-u.s-and-international",
  PRIVACY_POLICY: "https://docs.brahma.fi/brahma-cards-or-legal/privacy-policy",
  BRAHMA_TERMS: "https://docs.brahma.fi/brahma-cards-or-legal",
};

interface CheckboxState {
  eSignConsent: boolean;
  accountOpening: boolean;
  brahmaTerms: boolean;
  informationAccuracy: boolean;
  unauthorizedSolicitation: boolean;
  residencyAndProhibition: boolean;
}

interface CheckboxProps {
  checkboxState: CheckboxState;
  onCheckboxChange: (key: keyof CheckboxState, value: boolean) => void;
}

// US Resident Checkboxes Component
const USResidentCheckboxes: React.FC<CheckboxProps> = ({
  checkboxState,
  onCheckboxChange,
}) => {
  return (
    <>
      <Checkbox
        checked={checkboxState.eSignConsent}
        onChange={(value) => onCheckboxChange("eSignConsent", value)}
        label={
          <>
            I accept the{" "}
            <a
              href={LINKS.E_SIGN_CONSENT}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              E-Sign Consent
            </a>
          </>
        }
        className="rounded-t-md"
      />
      <Checkbox
        checked={checkboxState.accountOpening}
        onChange={(value) => onCheckboxChange("accountOpening", value)}
        label={
          <>
            I accept the{" "}
            <a
              href={LINKS.US_ACCOUNT_OPENING}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Account Opening Privacy Notice
            </a>
          </>
        }
      />
      <Checkbox
        checked={checkboxState.brahmaTerms}
        onChange={(value) => onCheckboxChange("brahmaTerms", value)}
        label={
          <>
            I accept the{" "}
            <a
              href={LINKS.US_TERMS}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Brahma Card Terms
            </a>
            , and the{" "}
            <a
              href={LINKS.PRIVACY_POLICY}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Brahma.Fi Privacy Policy
            </a>
          </>
        }
      />
      <Checkbox
        checked={checkboxState.informationAccuracy}
        onChange={(value) => onCheckboxChange("informationAccuracy", value)}
        label="I certify that the information I have provided is accurate and that I will abide by all the rules and requirements related to my Brahma Spend Card."
      />
      <Checkbox
        checked={checkboxState.unauthorizedSolicitation}
        onChange={(value) =>
          onCheckboxChange("unauthorizedSolicitation", value)
        }
        label="I acknowledge that applying for the Brahma Spend Card does not constitute unauthorized solicitation."
      />
      <Checkbox
        checked={checkboxState.residencyAndProhibition}
        onChange={(value) => onCheckboxChange("residencyAndProhibition", value)}
        label={
          <>
            I confirm I'm a U.S. resident, I have reviewed the{" "}
            <a
              href={LINKS.PROHIBITED_ACTIVITIES}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Prohibition list
            </a>
            , & comply with local laws.
          </>
        }
        className="rounded-b-md"
      />
    </>
  );
};

// Non-US Resident Checkboxes Component
const NonUSResidentCheckboxes: React.FC<CheckboxProps> = ({
  checkboxState,
  onCheckboxChange,
}) => {
  return (
    <>
      <Checkbox
        checked={checkboxState.eSignConsent}
        onChange={(value) => onCheckboxChange("eSignConsent", value)}
        label={
          <>
            I accept the{" "}
            <a
              href={LINKS.E_SIGN_CONSENT}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              E-Sign Consent
            </a>
          </>
        }
        className="rounded-t-md"
      />
      <Checkbox
        checked={checkboxState.brahmaTerms}
        onChange={(value) => onCheckboxChange("brahmaTerms", value)}
        label={
          <>
            I accept the{" "}
            <a
              href={LINKS.INTERNATIONAL_TERMS}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Brahma Card Terms
            </a>
            , and the{" "}
            <a
              href={LINKS.PRIVACY_POLICY}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Brahma.Fi Privacy Policy
            </a>
          </>
        }
      />
      <Checkbox
        checked={checkboxState.informationAccuracy}
        onChange={(value) => onCheckboxChange("informationAccuracy", value)}
        label="I certify that the information I have provided is accurate and that I will abide by all the rules and requirements related to my Brahma Spend Card."
      />
      <Checkbox
        checked={checkboxState.unauthorizedSolicitation}
        onChange={(value) =>
          onCheckboxChange("unauthorizedSolicitation", value)
        }
        label="I acknowledge that applying for the Brahma Spend Card does not constitute unauthorized solicitation."
      />
      <Checkbox
        checked={checkboxState.residencyAndProhibition}
        onChange={(value) => onCheckboxChange("residencyAndProhibition", value)}
        label={
          <>
            I confirm I'm not a U.S. resident, I have reviewed the{" "}
            <a
              href={LINKS.PROHIBITED_ACTIVITIES}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Prohibition list
            </a>
            , & comply with local laws.
          </>
        }
        className="rounded-b-md"
      />
    </>
  );
};

export default function PreRainTerms() {
  const [checkboxState, setCheckboxState] = useState<CheckboxState>({
    eSignConsent: false,
    accountOpening: false,
    brahmaTerms: false,
    informationAccuracy: false,
    unauthorizedSolicitation: false,
    residencyAndProhibition: false,
  });

  const { setAcceptedRainTerms } = useKycStore();
  const [isUsResident, setIsUsResident] = useState<null | boolean>(null);
  const { eoaAddress } = useSDKConfigStore();

  useEffect(() => {
    if (!eoaAddress) return;
    const checkResidency = async () => {
      try {
        const res = await getUserIsUs();
        setIsUsResident(res.data);
      } catch {
        setIsUsResident(false); // fallback to not US resident
      }
    };
    checkResidency();
  }, [eoaAddress]);

  const handleCheckboxChange = (key: keyof CheckboxState, value: boolean) => {
    setCheckboxState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Calculate if all required checkboxes are checked
  const allChecked = isUsResident
    ? Object.values(checkboxState).every(Boolean) // US residents need all 6 checkboxes
    : checkboxState.eSignConsent &&
      checkboxState.brahmaTerms &&
      checkboxState.informationAccuracy &&
      checkboxState.unauthorizedSolicitation &&
      checkboxState.residencyAndProhibition; // Non-US residents need 5 checkboxes (no accountOpening)

  if (isUsResident === null) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <div className="text-[#CBCED1] font-['Neue Montreal'] text-[18px] font-medium leading-[22.5px]">
          Checking residency...
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full pt-4">
      <div className="text-[#CBCED1] font-['Neue Montreal'] text-[18px] font-medium pb-4">
        By continuing you accept our terms
      </div>
      <div className="flex flex-col gap-1 w-full">
        {isUsResident ? (
          <USResidentCheckboxes
            checkboxState={checkboxState}
            onCheckboxChange={handleCheckboxChange}
          />
        ) : (
          <NonUSResidentCheckboxes
            checkboxState={checkboxState}
            onCheckboxChange={handleCheckboxChange}
          />
        )}
      </div>
      <button
        className={`flex h-10 mt-4 justify-center items-center gap-1 w-full rounded-lg bg-white backdrop-blur-[6px] text-black font-medium ${
          !allChecked ? "opacity-50 bg-gray-400 cursor-not-allowed" : ""
        }`}
        disabled={!allChecked}
        onClick={() => setAcceptedRainTerms(true)}
      >
        Continue
      </button>
    </div>
  );
}
